import {
	Breakpoints,
	ContainerGutter,
	ContainerPadding,
	CTA,
	FilterTag,
	Layout,
	Spinner,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
	Image as ComponentsCoreImage,
	Container,
} from '@volkswagen-onehub/components-core';
import React, { useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import Tesseract from 'tesseract.js';
import { ConfirmVehicleData } from './confirm-vehicle-data';
import { Upload } from '@volkswagen-onehub/icons-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import { HelpContainer } from './help-container';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { ErrorType } from '@esportal/portal-service';
import { formatOwnership, pageInPath } from '../../../tracking/trackingHelper';
import { PortalContext } from '../../../context/PortalContext';

const DropzoneContainer = styled.div`
	border: 2px dashed #00b0f0;
	text-align: center;
	cursor: pointer;
	border-radius: 16px;
	width: 418px;
`;

const RemoveButton = styled(FilterTag)`
	background-color: grey;
	color: black;
	cursor: pointer;
	margin-top: 10px;
`;

const IconStyled = styled(CTA)`
	background-color: white;
	color: #4cc7f4;

	& svg {
		height: 24px;
		width: 24px;
	}
`;

const validateAndCorrectPlate = (text: string): string | null => {
	const matchPlateOnlyNumbers = text.match(/(?<!\w)(\d{4})([A-Za-z]{3})(?!\w)/);
	const matchPlateNumberWithChar = text.match(
		/\b(\d{3}[A-Z]|\d{2}[A-Z]\d|\d[A-Z]\d{2}|[A-Z]\d{3})[B-DF-HJ-NP-TV-Z]{3}\b/i,
	);

	if (matchPlateOnlyNumbers) {
		return matchPlateOnlyNumbers[0].toUpperCase();
	} else if (matchPlateNumberWithChar) {
		const upperText = matchPlateNumberWithChar[0].toUpperCase();
		const correctNumbers = upperText
			.slice(0, 4)
			.replace(/O/g, '0')
			.replace(/I/g, '1')
			.replace(/Z/g, '2')
			.replace(/B/g, '8')
			.replace(/S/g, '5')
			.replace(/G/g, '6')
			.replace(/T/g, '7')
			.replace(/Q/g, '9')
			.replace(/D/g, '0');
		return correctNumbers + upperText.slice(4);
	}

	return null;
};

const validateAndCorrectVin = (text: string): string | null => {
	const vinMatch = text.match(/\b[wWvV][vV][A-Za-z][A-Za-z0-9]{14}\b/);

	if (vinMatch) {
		return vinMatch[0].replace(/I/gi, '1').replace(/[OQ]/gi, '0').toUpperCase();
	}

	return null;
};

// Función para aplicar preprocesamiento (binarización y contraste)
const preprocessImage = (imageUrl: string): Promise<HTMLCanvasElement> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'Anonymous'; // Importante para evitar problemas de CORS
		img.src = imageUrl;

		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject('Contexto de canvas no disponible');
				return;
			}

			canvas.width = img.width;
			canvas.height = img.height;
			ctx.drawImage(img, 0, 0);

			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const data = imageData.data;

			// Eliminar ruido y ajustar contraste
			const contrast = 128; // Puedes ajustar este valor
			const contrastFactor = (259 * (contrast + 255)) / (255 * (259 - contrast));

			for (let i = 0; i < data.length; i += 4) {
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];
				let brightness = 0.299 * r + 0.587 * g + 0.114 * b;

				// Ajuste de contraste
				brightness = contrastFactor * (brightness - 128) + 128;

				const binarizedValue = brightness > 128 ? 255 : 0;
				data[i] = data[i + 1] = data[i + 2] = binarizedValue; // Convertir a blanco y negro
			}

			ctx.putImageData(imageData, 0, 0);
			resolve(canvas);
		};

		img.onerror = () => {
			reject('Error al cargar la imagen');
		};
	});
};

export const CreateVehicle: React.FC<{
	setIsJourneyLayerVisible?: React.Dispatch<React.SetStateAction<boolean>>;
	setIsAlertLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	layerRef: React.RefObject<LayerHandleV2<any>>;
	registerToClub?: boolean;
	setErrorType: React.Dispatch<React.SetStateAction<ErrorType>>;
}> = ({ setIsJourneyLayerVisible, setIsAlertLayerVisible, layerRef, registerToClub }) => {
	const { portalService } = useContext(PortalContext);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const [fichaTecnicaFileName, setFichaTecnicaFileName] = React.useState<string>('Eliminar ficha técnica'); // Nombre del archivo de la ficha tecnica
	const [permisoCirculacionFileName, setPermisoCirculacionFileName] = React.useState<string>(
		'Eliminar permiso de circulación',
	); // Nombre del archivo del permiso de circulacion
	const [fichaTecnica, setFichaTecnica] = React.useState<File | null>(null); // Imagen de la ficha técnica
	const [permisoCirculacion, setPermisoCirculacion] = React.useState<File | null>(null); // Imagen del permiso de circulación
	const [isLoadingFicha, setIsLoadingFicha] = React.useState(false); // Estado de carga para la ficha técnica
	const [isLoadingPermiso, setIsLoadingPermiso] = React.useState(false); // Estado de carga para el permiso de circulación
	const [resultFicha, setResultFicha] = React.useState<{ vin: string | null; plate: string | null }>({
		vin: null,
		plate: null,
	}); // Resultado para la ficha técnica
	const [resultPermiso, setResultPermiso] = React.useState<{ vin: string | null; plate: string | null }>({
		vin: null,
		plate: null,
	}); // Resultado para el permiso de circulación
	const [finalData, setFinalData] = React.useState<{ vin: string | null; plate: string | null }>({
		vin: null,
		plate: null,
	});
	const [isFichaTecnicaLoaded, setIsFichaTecnicaLoaded] = React.useState(false);
	const [isPermisoCirculacionLoaded, setIsPermisoCirculacionLoaded] = React.useState(false);
	const [confirmVehicleData, setConfirmVehicleData] = React.useState(false);
	const [isSmallScreen, setIsSmallScreen] = React.useState(window.innerWidth < 1280);

	const trackingManager = useTrackingManager();

	const availableLanguages = ['oci', 'cym'];
	//idioma retirado: 'que'
	const processOCRWithLanguages = async (imgDataUrl: string) => {
		const preprocessedCanvas = await preprocessImage(imgDataUrl);
		const preprocessedImage = preprocessedCanvas.toDataURL();
		let tempVin: string | null = null;
		let tempPlate: string | null = null;

		for (const lang of availableLanguages) {
			try {
				const {
					data: { text },
				} = await Tesseract.recognize(preprocessedImage, lang);
				const cleanText = text
					.replace(/[_]/g, '')
					.replace(/[^\w\s]/g, '')
					.replace(/\s+/g, ' ')
					.trim();

				const vin = validateAndCorrectVin(cleanText);
				const plate = validateAndCorrectPlate(cleanText);

				if (vin && plate) {
					return { vin: vin, plate: plate };
				}

				tempVin = tempVin || vin;
				tempPlate = tempPlate || plate;

				if (tempVin && tempPlate) {
					return { vin: tempVin, plate: tempPlate };
				}
			} catch (error) {
				console.error(`Error procesando OCR en el idioma ${lang}:`, error);
			}
		}

		return { vin: tempVin, plate: tempPlate };
	};

	const processFile = async (file: string, setResult: Function, setIsLoading: Function, type: string) => {
		setIsLoading(true); // Iniciar carga

		const result = await processOCRWithLanguages(file); // Procesar OCR con múltiples idiomas

		setResult({
			vin: result.vin,
			plate: result.plate,
		});

		// Actualiza el estado dependiendo del tipo de archivo

		if (type == 'fichaTecnica') {
			setIsFichaTecnicaLoaded(true);
		} else if (type == 'permisoCirculacion') {
			setIsPermisoCirculacionLoaded(true);
		}

		setIsLoading(false); // Finalizar carga
	};

	React.useEffect(() => {
		// Verificar que ambos procesos han terminado
		if (!isLoadingFicha && !isLoadingPermiso) {
			const commonVin =
				resultFicha.vin && resultPermiso.vin
					? resultFicha.vin === resultPermiso.vin
						? resultFicha.vin
						: null
					: resultFicha.vin || resultPermiso.vin || null;

			// Comparar y obtener la matrícula
			const commonPlate =
				resultFicha.plate && resultPermiso.plate
					? resultFicha.plate === resultPermiso.plate
						? resultFicha.plate
						: null
					: resultFicha.plate || resultPermiso.plate || null;

			setFinalData({ vin: commonVin, plate: commonPlate });

			console.log('Resultado ficha:', { vin: resultFicha.vin, plate: resultFicha.plate });
			console.log('Resultado permiso:', { vin: resultPermiso.vin, plate: resultPermiso.plate });
			console.log('Resultado final:', { vin: commonVin, plate: commonPlate });
		}
	}, [resultFicha, resultPermiso, isLoadingFicha, isLoadingPermiso]);

	React.useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth < 1280);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	React.useEffect(() => {
		if (registerToClub) {
			pageInPath('add-vehicle/documents.app');
			trackingManager.trackClubVWGenericFeatureAppLoad({ contentId: 'add-vehicle/documents' }, {}, { vehicle: [] });
		}
	}, []);

	const handleDropZoneClick = (contentId: string, copy: string) => {
		trackingManager.trackClubVWButtonClick(
			copy,
			{
				contentId: `${contentId}`,
			},
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
	};

	React.useEffect(() => {
		if (userVehicles && !registerToClub) {
			trackingManager.trackClubVWLayerLoad(
				{ contentId: 'Create vehicle' },
				{ vehicle: formatOwnership(userVehicles.vehicles) },
			);
		}
	}, []);

	const handleNextOnClick = () => {
		setConfirmVehicleData(true);
		trackingManager.trackClubVWButtonClick(
			'Siguiente',
			{
				contentId: 'Upload documents',
			},
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
	};

	// Dropzone para la ficha técnica
	const { getRootProps: getRootPropsFichaTecnica, getInputProps: getInputPropsFichaTecnica } = useDropzone({
		accept: { 'image/*': [] },
		maxSize: 10 * 1000 * 1000,
		onDrop: (acceptedFiles) => {
			if (acceptedFiles.length === 0) return;
			const file = acceptedFiles[0];
			setFichaTecnica(file);
			const reader = new FileReader();
			reader.onload = () => {
				const fileData = reader.result as string;
				processFile(fileData, setResultFicha, setIsLoadingFicha, 'fichaTecnica');
				setFichaTecnicaFileName(processFileName(file.name));
			};
			reader.readAsDataURL(file);
		},
	});

	// Dropzone para el permiso de circulación
	const { getRootProps: getRootPropsPermisoCirculacion, getInputProps: getInputPropsPermisoCirculacion } = useDropzone({
		accept: { 'image/*': [] },
		disabled: !!permisoCirculacion,
		maxSize: 10 * 1000 * 1000,
		onDrop: (acceptedFiles) => {
			if (acceptedFiles.length === 0) return;
			const file = acceptedFiles[0];
			setPermisoCirculacion(file);
			const reader = new FileReader();
			reader.onload = () => {
				const fileData = reader.result as string;
				processFile(fileData, setResultPermiso, setIsLoadingPermiso, 'permisoCirculacion');
				setPermisoCirculacionFileName(processFileName(file.name));
			};
			reader.readAsDataURL(file);
		},
	});

	const processFileName = (fileName: string): string => {
		return fileName.length > 60 ? fileName.slice(-60) : fileName;
	};

	// Funciones para eliminar archivos subidos
	const removeFichaTecnica = () => {
		setFichaTecnica(null);
		setIsFichaTecnicaLoaded(false);
	};

	const removePermisoCirculacion = () => {
		setPermisoCirculacion(null);
		setIsPermisoCirculacionLoaded(false);
	};

	return (
		<>
			{confirmVehicleData && fichaTecnica && permisoCirculacion ? (
				<ConfirmVehicleData
					vehicles={userVehicles?.vehicles}
					vin={finalData.vin}
					plate={finalData.plate}
					fichaTecnica={fichaTecnica}
					permisoCirculacion={permisoCirculacion}
					setIsJourneyLayerVisible={setIsJourneyLayerVisible}
					setIsAlertLayerVisible={setIsAlertLayerVisible}
					layerRef={layerRef}
					registerToClub={registerToClub}
				/>
			) : (
				<Layout
					data-testid="add-car-layout"
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 22 },
							{ name: '.', columns: 1 },
						],
						[Breakpoints.b1280]: [
							{ name: '.', columns: 2 },
							{ name: 'a', columns: 20 },
							{ name: '.', columns: 2 },
						],
					}}
					allowOverflowingContent
				>
					<Container
						padding={{ top: ContainerPadding.dynamic0100, bottom: ContainerPadding.dynamic0100 }}
						horizontalAlign={'center'}
						wrap={'always'}
					>
						{registerToClub ? (
							<>
								<Container
									padding={{ bottom: ContainerPadding.dynamic0200 }}
									wrap={'always'}
									horizontalAlign={'center'}
									stretchContent
								>
									<Container horizontalAlign={'center'}>
										<ComponentsCoreImage
											src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-club/logo_club.svg"
											alt="logoClub"
										/>
									</Container>
								</Container>
								<Container
									horizontalAlign={'center'}
									wrap={'always'}
									padding={{ right: ContainerPadding.grid001, left: ContainerPadding.grid001 }}
								>
									<Container wrap={'always'} padding={{ bottom: ContainerPadding.dynamic0130 }}>
										<Text
											appearance={TokenTextAppearance.headline300}
											textAlign={TextAlignment.center}
											color={TokenTextColor.primary}
											bold
										>
											¿Ya tienes un Volkswagen?
										</Text>
									</Container>
									<Text
										appearance={TokenTextAppearance.headline300}
										textAlign={TextAlignment.center}
										color={TokenTextColor.primary}
									>
										Entonces necesitaremos estos datos para añadirlo.
									</Text>
								</Container>
							</>
						) : (
							<Text
								appearance={TokenTextAppearance.headline300}
								textAlign={TextAlignment.center}
								color={TokenTextColor.primary}
							>
								Necesitamos algunos datos para añadir tu coche. ¿Empezamos?
							</Text>
						)}

						{/* Dropzone para el permiso de circulación */}
						<Container
							stretchContent
							wrap={'always'}
							padding={{
								top: ContainerPadding.dynamic0250,
							}}
						>
							<Container
								horizontalAlign={'center'}
								verticalAlign={'center'}
								padding={{
									bottom: ContainerPadding.dynamic0100,
								}}
							>
								<Text
									appearance={TokenTextAppearance.bigcopy100}
									textAlign={TextAlignment.center}
									color={TokenTextColor.primary}
								>
									Primero, el permiso o autorización de circulación de tu coche
								</Text>
							</Container>

							{isLoadingPermiso ? (
								<Container
									verticalAlign={'center'}
									horizontalAlign={'center'}
									padding={{
										top: ContainerPadding.dynamic0200,
									}}
								>
									<Spinner variant="large" />
								</Container>
							) : permisoCirculacion ? (
								<Container
									verticalAlign={'center'}
									horizontalAlign={'center'}
									padding={{
										top: ContainerPadding.dynamic0200,
									}}
								>
									<RemoveButton
										iconPosition="end"
										text={permisoCirculacionFileName}
										active
										onClick={removePermisoCirculacion}
									/>
								</Container>
							) : (
								<>
									<Container
										horizontalAlign={'center'}
										padding={{
											bottom: ContainerPadding.dynamic0020,
										}}
									>
										<img
											src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/fotos-ejemplo/EjemploPermisoCirculacion.jpg"
											height="150"
											alt="Permiso de circulación"
										/>
									</Container>
									<Container
										horizontalAlign={'center'}
										padding={{
											bottom: ContainerPadding.dynamic0100,
										}}
									>
										<Text
											appearance={TokenTextAppearance.copy100}
											textAlign={TextAlignment.center}
											color={TokenTextColor.primary}
										>
											Sube una foto o archivo del interior del permiso de circulación de tu Volkswagen o de la
											autorización de circulación en el caso de tener un renting.
										</Text>
									</Container>

									<Container horizontalAlign={'center'} verticalAlign={'center'}>
										{isSmallScreen ? (
											<>
												<div
													{...getRootPropsPermisoCirculacion({
														onClick: () => {
															handleDropZoneClick('Upload permiso de circulación', 'Subir permiso de circulación');
														},
													})}
												>
													<input
														{...getInputPropsPermisoCirculacion()}
														style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0 }}
													/>
													<CTA
														tag="button"
														emphasis="primary"
														icon={
															<Text>
																<Upload variant="small" />
															</Text>
														}
													>
														<Text>Subir permiso de circulación</Text>
													</CTA>
												</div>
												<Container wrap={'always'} padding={{ top: ContainerPadding.dynamic0020 }}>
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.secondary}
													>
														Tamaño máximo: 10 MB
													</Text>
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.secondary}
													>
														Archivo soportado: JPG y PNG
													</Text>
												</Container>
											</>
										) : (
											<DropzoneContainer
												{...getRootPropsPermisoCirculacion({
													onClick: () => {
														handleDropZoneClick('Upload permiso de circulación', 'Subir permiso de circulación');
													},
												})}
											>
												<Container
													wrap={'always'}
													padding={ContainerPadding.static150}
													gutter={ContainerGutter.static100}
													stretchContent
												>
													<input {...getInputPropsPermisoCirculacion()} />
													<IconStyled tag="a" icon={<Upload variant="small" aria-hidden />} />
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.primary}
														bold
													>
														Subir permiso de circulación
													</Text>
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.secondary}
													>
														Tamaño máximo: 10 MB. Archivo soportado: JPG y PNG
													</Text>
												</Container>
											</DropzoneContainer>
										)}
									</Container>
								</>
							)}
						</Container>

						{/* Dropzone para la ficha técnica */}
						<Container
							padding={{
								top: ContainerPadding.dynamic0250,
							}}
							stretchContent
							wrap={'always'}
						>
							<Container
								padding={{ bottom: ContainerPadding.dynamic0100 }}
								horizontalAlign={'center'}
								verticalAlign={'center'}
							>
								<Text
									appearance={TokenTextAppearance.bigcopy100}
									textAlign={TextAlignment.center}
									color={TokenTextColor.primary}
								>
									Y ahora la ficha técnica
								</Text>
							</Container>

							{isLoadingFicha ? (
								<Container
									verticalAlign={'center'}
									horizontalAlign={'center'}
									padding={{
										top: ContainerPadding.dynamic0200,
									}}
								>
									<Spinner variant="large" />
								</Container>
							) : fichaTecnica ? (
								<Container
									verticalAlign={'center'}
									horizontalAlign={'center'}
									padding={{
										top: ContainerPadding.dynamic0200,
									}}
								>
									<RemoveButton iconPosition="end" text={fichaTecnicaFileName} active onClick={removeFichaTecnica} />
								</Container>
							) : (
								<>
									<Container
										horizontalAlign={'center'}
										padding={{
											bottom: ContainerPadding.dynamic0020,
										}}
									>
										<img
											src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/fotos-ejemplo/EjemploFichaTecnica.jpeg"
											height="150"
											alt="Ficha técnica"
										/>
									</Container>

									<Text
										appearance={TokenTextAppearance.copy100}
										textAlign={TextAlignment.center}
										color={TokenTextColor.primary}
									>
										Sube una foto o archivo de la tarjeta de inspección técnica donde se acredite que tu Volkswagen
										puede circular por España.
									</Text>

									<Container
										padding={{ top: ContainerPadding.dynamic0100 }}
										horizontalAlign={'center'}
										verticalAlign={'center'}
									>
										{isSmallScreen ? (
											<>
												<div
													{...getRootPropsFichaTecnica({
														onClick: () => {
															handleDropZoneClick('Upload ficha técnica', 'Subir ficha técnica');
														},
													})}
												>
													<input
														{...getInputPropsFichaTecnica()}
														style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0 }}
													/>
													<CTA
														tag="button"
														emphasis="primary"
														icon={
															<Text>
																<Upload variant="small" />
															</Text>
														}
													>
														<Text>Subir ficha técnica</Text>
													</CTA>
												</div>
												<Container wrap={'always'} padding={{ top: ContainerPadding.dynamic0020 }}>
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.secondary}
													>
														Tamaño máximo: 10 MB
													</Text>
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.secondary}
													>
														Archivo soportado: JPG y PNG
													</Text>
												</Container>
											</>
										) : (
											<DropzoneContainer
												{...getRootPropsFichaTecnica({
													onClick: () => {
														handleDropZoneClick('Upload ficha técnica', 'Subir ficha técnica');
													},
												})}
											>
												<Container
													wrap={'always'}
													padding={ContainerPadding.static150}
													gutter={ContainerGutter.static100}
													stretchContent
												>
													<input {...getInputPropsFichaTecnica()} />
													<IconStyled tag="a" icon={<Upload variant="small" aria-hidden />} />
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.primary}
													>
														Subir ficha técnica
													</Text>
													<Text
														appearance={TokenTextAppearance.label150}
														textAlign={TextAlignment.center}
														color={TokenTextColor.secondary}
													>
														Tamaño máximo: 10 MB. Archivo soportado: JPG y PNG
													</Text>
												</Container>
											</DropzoneContainer>
										)}
									</Container>
								</>
							)}
						</Container>

						<Container
							horizontalAlign={'center'}
							padding={{
								top: ContainerPadding.dynamic0250,
								bottom: ContainerPadding.dynamic0250,
							}}
						>
							<CTA
								tag="button"
								emphasis="primary"
								size={'large'}
								disabled={!isFichaTecnicaLoaded || !isPermisoCirculacionLoaded}
								onClick={handleNextOnClick}
							>
								<Text>Siguiente</Text>
							</CTA>
						</Container>
						<HelpContainer />
					</Container>
				</Layout>
			)}
		</>
	);
};
